/**
 * Everything that needs the AudioContext, built on the first gesture: the buses, the SID voices
 * with their cap, the effects, the ambience and the synthesised music, and the one timer that
 * keeps the scheduled parts fed.
 */

import type { SimEvent } from '../sim/types';
import { AmbienceSound } from './ambience';
import { Bus } from './bus';
import { TICK_MS } from './music/clock';
import { MusicPlayer } from './music/player';
import { compiledSong, variantShift } from './music/songs';
import { mulberry32, type Rand } from './rand';
import { AMBIENT, collapse, CUE, DUCKS, loudness, PRIORITY } from './scan';
import { CUE_SFX, SFX, UI_SFX } from './sfx';
import { type Playing, Sid } from './sid';
import type { Ambience, Cue, MusicTrack, MusicVariant, UiSound, Volumes } from './types';
import type { VoiceSpec } from './voice';
import { admit, expire, type Slot } from './voicePool';

/** The variation stream's seed: any constant will do, so a session's sounds vary the same way each run. */
const SEED = 0x5704ef;

type Held = Slot & { readonly voice: Playing };

export class Engine {
  private readonly bus: Bus;
  private readonly sid: Sid;
  private readonly rand: Rand = mulberry32(SEED);
  private readonly player: MusicPlayer;
  private readonly amb: AmbienceSound;
  private held: Held[] = [];

  constructor(Ctor: typeof AudioContext, volumes: Volumes) {
    this.bus = new Bus(Ctor, volumes);
    this.sid = new Sid(this.bus.ctx, this.bus.noise, this.bus.noiseHz);
    this.player = new MusicPlayer(this.bus, this.sid);
    const blip = (voices: VoiceSpec[], at: number): void => this.fire(voices, AMBIENT, 1, 0, this.bus.sfx, at);
    this.amb = new AmbienceSound(this.bus, blip, this.rand);
    // One timer for the page's life: the audio object lives as long as the game does.
    setInterval(() => this.tick(), TICK_MS);
  }

  resume(): void {
    this.bus.resume();
  }

  setVolumes(v: Volumes): void {
    this.bus.setVolumes(v);
  }

  events(events: readonly SimEvent[], listenerX: number): void {
    if (events.length === 0 || !this.bus.live) return;
    let duck = false;
    for (const hit of collapse(events, listenerX)) {
      const voices = SFX[hit.sound](this.rand, hit.n);
      this.fire(voices, PRIORITY[hit.sound], loudness(hit.count), hit.pan, this.bus.sfx, this.bus.now);
      if (DUCKS.has(hit.sound)) duck = true;
    }
    if (duck) this.bus.duck();
  }

  ambience(a: Ambience): void {
    this.amb.set(a);
  }

  ui(s: UiSound): void {
    if (this.bus.live) this.fire(UI_SFX[s](this.rand, 0), CUE, 1, 0, this.bus.ui, this.bus.now);
  }

  cue(c: Cue, n: number): void {
    // Paused, the cave's continuous sounds stop; the game's next ambience() call brings them back.
    if (c === 'pause') this.amb.set({ amoeba: false, magic: false, cover: false });
    if (!this.bus.live) return;
    const out = c === 'bonusTick' || c === 'bonusLife' ? this.bus.sfx : this.bus.ui;
    this.fire(CUE_SFX[c](this.rand, n), CUE, 1, 0, out, this.bus.now);
  }

  /** The synthesised track, from the top (or carrying on, if it is already the one playing). */
  playSong(track: MusicTrack, variant: MusicVariant | undefined): void {
    this.player.play(`${track}/${variant ?? ''}`, compiledSong(track), variantShift(track, variant));
  }

  stopSong(): void {
    this.player.stop();
  }

  /** Voices through the cap: each is admitted, steals an older / lesser one, or is dropped. */
  private fire(
    voices: readonly VoiceSpec[], priority: number, gain: number, pan: number, out: AudioNode, t0: number,
  ): void {
    this.held = expire(this.held, this.bus.now);
    for (const v of voices) {
      const verdict = admit(this.held, priority);
      if (verdict.kind === 'drop') continue;
      if (verdict.kind === 'steal') {
        const [victim] = this.held.splice(verdict.index, 1);
        victim?.voice.cut(this.bus.now);
      }
      const voice = this.sid.play(v, t0, out, gain, pan);
      this.held.push({ start: voice.start, end: voice.end, priority, voice });
    }
  }

  private tick(): void {
    this.player.tick();
    this.amb.tick();
  }
}
